// AI Recording Studio Pro — SSH Manager
// Manage SSH connections, categories, and credentials
import { useState, useEffect } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import {
  Server, Plus, Search, Star, Trash2, Pencil, MoreVertical, Copy,
  Terminal, Wifi, WifiOff, Key, Lock, Shield, Globe, Cloud,
  Monitor, Database, HardDrive, Tag, FolderOpen, RefreshCw,
  CheckCircle2, XCircle, Clock, ExternalLink, Play,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { sshAPI } from "@/lib/api";

const CATEGORIES = [
  { id: "lab", label: "Lab", icon: Monitor, color: "#6366f1" },
  { id: "dev", label: "Développement", icon: Terminal, color: "#22c55e" },
  { id: "staging", label: "Pré-production", icon: Database, color: "#f97316" },
  { id: "prod", label: "Production", icon: Shield, color: "#ef4444" },
  { id: "cloud", label: "Cloud", icon: Cloud, color: "#3b82f6" },
  { id: "client", label: "Clients", icon: Globe, color: "#8b5cf6" },
  { id: "other", label: "Autres", icon: Server, color: "#64748b" },
];

const AUTH_METHODS = [
  { value: "password", label: "Mot de passe" },
  { value: "key", label: "Clé SSH" },
  { value: "key_password", label: "Clé SSH + passphrase" },
];

const EMPTY_CONNECTION = {
  name: "", host: "", port: "22", username: "root", auth_method: "password",
  password: "", private_key: "", passphrase: "", category: "lab",
  jump_host: "", jump_port: "22", jump_username: "", tags: "",
  description: "", is_favorite: false,
};

const STORAGE_KEY = "studio_ssh_connections";              // legacy client-only store
const MIGRATED_KEY = "studio_ssh_connections_migrated";     // one-time migration flag

// Fields the backend accepts (drops runtime-only fields such as id/status)
const CONNECTION_FIELDS = [
  "name", "host", "port", "username", "auth_method", "password", "private_key",
  "passphrase", "category", "jump_host", "jump_port", "jump_username",
  "tags", "description", "is_favorite",
];

function toPayload(conn) {
  const out = {};
  for (const f of CONNECTION_FIELDS) {
    if (conn[f] !== undefined && conn[f] !== null) out[f] = conn[f];
  }
  return out;
}

export default function RecordingSSHManager() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CONNECTION });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});

  // Reload connections from the backend (single source of truth)
  const refresh = async () => {
    try {
      const { data } = await sshAPI.list();
      setConnections(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Impossible de charger les connexions SSH");
    } finally {
      setLoading(false);
    }
  };

  // Load on mount + one-time migration of any connections saved in the old localStorage store
  useEffect(() => {
    (async () => {
      try {
        if (!localStorage.getItem(MIGRATED_KEY)) {
          const legacyRaw = localStorage.getItem(STORAGE_KEY);
          if (legacyRaw) {
            const legacy = JSON.parse(legacyRaw);
            if (Array.isArray(legacy) && legacy.length) {
              const { data: existing } = await sshAPI.list();
              if (!existing || existing.length === 0) {
                // skip built-in demo entries; only migrate real user-added connections
                const realOnes = legacy.filter((c) => !String(c.id || "").startsWith("demo"));
                for (const c of realOnes) {
                  try { await sshAPI.create(toPayload(c)); } catch {}
                }
              }
            }
          }
          localStorage.setItem(MIGRATED_KEY, "1");
        }
      } catch {}
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter connections
  const filtered = connections.filter((c) => {
    if (categoryFilter !== "all" && c.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.host.toLowerCase().includes(q) ||
        c.username.toLowerCase().includes(q) || (c.tags || "").toLowerCase().includes(q);
    }
    return true;
  });

  const favorites = filtered.filter((c) => c.is_favorite);
  const others = filtered.filter((c) => !c.is_favorite);

  // Open dialog
  const openNew = () => { setForm({ ...EMPTY_CONNECTION }); setEditId(null); setShowDialog(true); };
  const openEdit = (conn) => { setForm({ ...conn }); setEditId(conn.id); setShowDialog(true); };

  // Save (create or update) — persisted on the backend
  const handleSave = async () => {
    if (!form.name.trim() || !form.host.trim()) return toast.error("Nom et hôte requis");
    setSaving(true);
    try {
      if (editId) {
        await sshAPI.update(editId, toPayload(form));
        toast.success("Connexion modifiée");
      } else {
        await sshAPI.create(toPayload(form));
        toast.success("Connexion ajoutée");
      }
      setShowDialog(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    try {
      await sshAPI.delete(id);
      setConnections((prev) => prev.filter((c) => c.id !== id));
      toast.success("Connexion supprimée");
    } catch (err) {
      toast.error("Échec de la suppression");
    }
  };

  // Toggle favorite (optimistic, reverts on failure)
  const toggleFavorite = async (id) => {
    const conn = connections.find((c) => c.id === id);
    if (!conn) return;
    const next = !conn.is_favorite;
    setConnections((prev) => prev.map((c) => c.id === id ? { ...c, is_favorite: next } : c));
    try {
      await sshAPI.update(id, { is_favorite: next });
    } catch (err) {
      setConnections((prev) => prev.map((c) => c.id === id ? { ...c, is_favorite: !next } : c));
      toast.error("Échec de la mise à jour");
    }
  };

  // Test connection (real SSH test via backend)
  const testConnection = async (id) => {
    const conn = connections.find((c) => c.id === id);
    if (!conn) return;
    setTesting((prev) => ({ ...prev, [id]: true }));
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${backendUrl}/api/recording-studio/ssh/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          host: conn.host,
          port: parseInt(conn.port) || 22,
          username: conn.username || "root",
          password: conn.password || "",
          private_key: conn.private_key || "",
        }),
      });
      const data = await res.json();
      const success = data.success === true;
      setConnections((prev) => prev.map((c) => c.id === id ? { ...c, status: success ? "online" : "offline" } : c));
      toast[success ? "success" : "error"](data.message || (success ? "Connexion réussie" : "Connexion échouée"));
    } catch (err) {
      setConnections((prev) => prev.map((c) => c.id === id ? { ...c, status: "offline" } : c));
      toast.error("Erreur réseau — impossible de tester la connexion");
    }
    setTesting((prev) => ({ ...prev, [id]: false }));
  };

  // Open the studio, whose terminal panel lists the saved connections
  const connectTerminal = (conn) => {
    navigate("/studio/record");
    toast.success(`Connexion à ${conn.host}...`);
  };

  // Connection Card
  const ConnectionCard = ({ conn, delay = 0 }) => {
    const cat = CATEGORIES.find((c) => c.id === conn.category) || CATEGORIES[6];
    const CatIcon = cat.icon;
    const isOnline = conn.status === "online";
    const isOffline = conn.status === "offline";

    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
        <Card className="group hover:shadow-md hover:border-primary/20 transition-all duration-200">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${cat.color}15`, color: cat.color }}>
                  <CatIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-sm font-semibold truncate">{conn.name}</h3>
                    {isOnline && <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />}
                    {isOffline && <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
                  </div>
                  <p className="text-[11px] text-muted-foreground font-mono truncate">
                    {conn.username}@{conn.host}:{conn.port}
                  </p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => connectTerminal(conn)} className="text-xs">
                    <Play className="h-3 w-3 mr-2" /> Connecter
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => testConnection(conn.id)} className="text-xs">
                    <RefreshCw className={`h-3 w-3 mr-2 ${testing[conn.id] ? "animate-spin" : ""}`} /> Tester
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEdit(conn)} className="text-xs">
                    <Pencil className="h-3 w-3 mr-2" /> Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toggleFavorite(conn.id)} className="text-xs">
                    <Star className={`h-3 w-3 mr-2 ${conn.is_favorite ? "fill-amber-400 text-amber-400" : ""}`} />
                    {conn.is_favorite ? "Retirer favori" : "Favori"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    navigator.clipboard.writeText(`ssh ${conn.username}@${conn.host} -p ${conn.port}`);
                    toast.success("Commande SSH copiée");
                  }} className="text-xs">
                    <Copy className="h-3 w-3 mr-2" /> Copier SSH
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleDelete(conn.id)} className="text-xs text-destructive">
                    <Trash2 className="h-3 w-3 mr-2" /> Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {conn.description && (
              <p className="text-[11px] text-muted-foreground line-clamp-1 mb-2">{conn.description}</p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[9px] px-1.5 py-0" style={{ borderColor: `${cat.color}40`, color: cat.color }}>
                {cat.label}
              </Badge>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                {conn.auth_method === "password" ? <Lock className="h-2.5 w-2.5 mr-0.5" /> : <Key className="h-2.5 w-2.5 mr-0.5" />}
                {AUTH_METHODS.find((a) => a.value === conn.auth_method)?.label || "Password"}
              </Badge>
              {(conn.tags || "").split(",").filter(Boolean).slice(0, 3).map((tag) => (
                <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{tag.trim()}</span>
              ))}
            </div>

            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/30">
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => connectTerminal(conn)}>
                <Terminal className="h-3 w-3" /> Connecter
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => testConnection(conn.id)}
                disabled={testing[conn.id]}>
                <RefreshCw className={`h-3 w-3 ${testing[conn.id] ? "animate-spin" : ""}`} />
                {testing[conn.id] ? "Test..." : "Tester"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #22c55e, #10b981)", boxShadow: "0 4px 12px rgba(34,197,94,0.3)" }}>
                <Server className="h-5 w-5 text-white" />
              </div>
              SSH Manager
            </h1>
            <p className="text-sm text-muted-foreground mt-1 ml-[52px]">
              {connections.length} connexion{connections.length !== 1 ? "s" : ""} configurées
            </p>
          </div>
          <Button size="sm" onClick={openNew} className="gap-1.5"
            style={{ background: "linear-gradient(135deg, #22c55e, #10b981)", border: "none" }}>
            <Plus className="h-4 w-4" /> Nouvelle connexion
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
          </div>
          <div className="flex gap-1 overflow-x-auto">
            <Button variant={categoryFilter === "all" ? "default" : "outline"} size="sm" className="h-7 text-[10px] px-2"
              onClick={() => setCategoryFilter("all")}>Tous</Button>
            {CATEGORIES.map((cat) => (
              <Button key={cat.id} variant={categoryFilter === cat.id ? "default" : "outline"} size="sm" className="h-7 text-[10px] px-2"
                onClick={() => setCategoryFilter(cat.id)}>
                <cat.icon className="h-3 w-3 mr-1" /> {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Favorites */}
        {favorites.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" /> Favoris
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {favorites.map((conn, i) => <ConnectionCard key={conn.id} conn={conn} delay={i * 0.03} />)}
            </div>
          </div>
        )}

        {/* All connections */}
        <div>
          {favorites.length > 0 && <h2 className="text-sm font-semibold mb-3">Toutes les connexions</h2>}
          {others.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {others.map((conn, i) => <ConnectionCard key={conn.id} conn={conn} delay={i * 0.03} />)}
            </div>
          ) : filtered.length === 0 && !loading && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Server className="h-12 w-12 text-muted-foreground/20" />
                <h3 className="font-semibold mt-4">Aucune connexion</h3>
                <p className="text-sm text-muted-foreground mt-1">Ajoutez vos serveurs SSH</p>
                <Button size="sm" onClick={openNew} className="mt-4 gap-1.5" variant="outline">
                  <Plus className="h-4 w-4" /> Ajouter
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Connection Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-sm">
                <Server className="h-4 w-4 text-primary" />
                {editId ? "Modifier la connexion" : "Nouvelle connexion SSH"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Nom *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Lab K8s Master" className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Hôte *</label>
                  <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
                    placeholder="192.168.1.10" className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Port</label>
                  <Input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })}
                    placeholder="22" className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Utilisateur</label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="root" className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Catégorie</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id} className="text-xs">{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Authentification</label>
                <Select value={form.auth_method} onValueChange={(v) => setForm({ ...form, auth_method: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUTH_METHODS.map((a) => <SelectItem key={a.value} value={a.value} className="text-xs">{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.auth_method === "password" && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Mot de passe</label>
                  <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••" className="h-8 text-xs" />
                </div>
              )}
              {(form.auth_method === "key" || form.auth_method === "key_password") && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-muted-foreground block">Clé privée SSH</label>
                    <label className="text-xs text-primary hover:underline cursor-pointer flex items-center gap-1">
                      <FolderOpen className="h-3 w-3" />
                      <span>Uploader la clé (.pem, .key, id_rsa...)</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setForm((prev) => ({ ...prev, private_key: event.target.result }));
                            toast.success(`Clé chargée avec succès depuis ${file.name}`);
                          };
                          reader.readAsText(file);
                        }}
                      />
                    </label>
                  </div>
                  <Textarea value={form.private_key || ""} onChange={(e) => setForm({ ...form, private_key: e.target.value })}
                    placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" rows={4} className="text-xs font-mono" />
                </div>
              )}
              {form.auth_method === "key_password" && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Passphrase</label>
                  <Input type="password" value={form.passphrase || ""} onChange={(e) => setForm({ ...form, passphrase: e.target.value })}
                    placeholder="••••••••" className="h-8 text-xs" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Tags (séparés par virgule)</label>
                <Input value={form.tags || ""} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="kubernetes, master, lab" className="h-8 text-xs" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Notes sur cette connexion..." rows={2} className="text-xs" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Jump Host (optionnel)</label>
                <div className="grid grid-cols-3 gap-2">
                  <Input value={form.jump_host || ""} onChange={(e) => setForm({ ...form, jump_host: e.target.value })}
                    placeholder="Hôte bastion" className="h-8 text-xs col-span-2" />
                  <Input value={form.jump_port || "22"} onChange={(e) => setForm({ ...form, jump_port: e.target.value })}
                    placeholder="Port" className="h-8 text-xs" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowDialog(false)}>Annuler</Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !form.name.trim() || !form.host.trim()}
                style={{ background: "linear-gradient(135deg, #22c55e, #10b981)", border: "none" }}>
                {saving ? "Enregistrement..." : editId ? "Modifier" : "Ajouter"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </RecordingStudioLayout>
  );
}
